import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Scale } from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import { useI18n } from '../lib/i18n'

const LABELS = {
  zh: {
    title: '欢迎回来',
    subtitle: '登录您的 AI 律师助手账号',
    email: '邮箱地址',
    password: '密码',
    login: '登录',
    logging: '登录中...',
    noAccount: '还没有账号？',
    register: '立即注册',
    forgotPassword: '忘记密码？',
    brandName: 'AI Lawyer',
    brandDesc: '智能法律助手',
    errorGeneric: '登录失败，请检查邮箱和密码',
  },
  en: {
    title: 'Welcome Back',
    subtitle: 'Sign in to your AI Lawyer account',
    email: 'Email',
    password: 'Password',
    login: 'Sign In',
    logging: 'Signing in...',
    noAccount: "Don't have an account?",
    register: 'Register now',
    forgotPassword: 'Forgot password?',
    brandName: 'AI Lawyer',
    brandDesc: 'Intelligent Legal Assistant',
    errorGeneric: 'Login failed. Please check your email and password.',
  },
}

export function Login() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const { login } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        setError(data?.detail || t.errorGeneric)
        return
      }
      const data = await res.json()
      login(data)
      navigate('/', { replace: true })
    } catch {
      setError(t.errorGeneric)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 items-center justify-center p-12">
        <div className="text-center">
          <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Scale className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t.brandName}</h1>
          <p className="text-blue-200 text-lg">{t.brandDesc}</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm mx-auto opacity-60">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-white/5 backdrop-blur" />
            ))}
          </div>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-800">{t.brandName}</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-1">{t.title}</h2>
          <p className="text-gray-500 mb-8">{t.subtitle}</p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.email}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.password}
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t.logging : t.login}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t.noAccount}{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              {t.register}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
